/**
 * The "Bundle is in beta" notice and the Beta label (design: https://claude.ai/artifact/HPcQhFwDizthDPZvNavoVt,
 * project/Beta.dc.html and project/BetaMobile.dc.html), and the one-top-banner-at-a-time slot it shares with the
 * offline banner and "Get the app". Pure: each app keeps `BetaState` per device and passes it in, so this ports to
 * Swift/Kotlin unchanged (vectors: packages/test-vectors/logic/beta).
 *
 * The banner shows from the first visit until it is dismissed or feedback is sent. After that it comes back once when
 * a new app version first runs (a version string different from the one it was closed on), and never sooner than
 * `REMIND_DAYS` after it was last closed. The Beta label always shows while `BETA` is on.
 */

/** Bundle is in beta. Turning this off hides the banner and the label everywhere. */
export const BETA = true;
/** The banner never returns sooner than this after it was dismissed or feedback was sent. */
export const REMIND_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** What a device remembers (all absent on the first visit). */
export interface BetaState {
  /** When × was last pressed (epoch ms). */
  dismissedAt?: number;
  /** The app version the banner was last closed on (by × or by sending feedback). */
  dismissedVersion?: string;
  /** When feedback was last sent successfully (epoch ms). */
  feedbackSentAt?: number;
}

/** The single banner above the shell: the connection banner first, then beta, then "Get the app". */
export type TopBanner = "offline" | "beta" | "getApp" | "none";

/** Which banners want the slot right now. */
export interface TopBannerWants {
  offline: boolean;
  beta: boolean;
  getApp: boolean;
}

/** Whether the beta banner shows. `beta` defaults to `BETA` (the vectors pass it to cover "off"). */
export function showBetaBanner(input: { state: BetaState; appVersion: string; nowMs: number; beta?: boolean }): boolean {
  const { state, appVersion, nowMs, beta = BETA } = input;
  if (!beta) return false;
  if (state.dismissedAt === undefined && state.feedbackSentAt === undefined) return true;
  if (state.dismissedVersion === appVersion) return false;
  const closedAt = Math.max(state.dismissedAt ?? 0, state.feedbackSentAt ?? 0);
  return nowMs - closedAt >= REMIND_DAYS * DAY_MS;
}

/** × on the banner. */
export function afterDismiss(input: { state: BetaState; appVersion: string; nowMs: number }): BetaState {
  return { ...input.state, dismissedAt: input.nowMs, dismissedVersion: input.appVersion };
}

/** Feedback was sent successfully (from the banner or anywhere else): the banner closes the same way as ×. */
export function afterFeedback(input: { state: BetaState; appVersion: string; nowMs: number }): BetaState {
  return { ...input.state, feedbackSentAt: input.nowMs, dismissedVersion: input.appVersion };
}

/**
 * Which banner holds the slot. The connection banner always wins; once a banner was closed this session
 * (`dismissedThisSession`), the next one waits for the next session instead of appearing right away.
 */
export function topBanner(input: TopBannerWants & { dismissedThisSession: boolean }): TopBanner {
  if (input.offline) return "offline";
  if (input.dismissedThisSession) return "none";
  if (input.beta) return "beta";
  if (input.getApp) return "getApp";
  return "none";
}

/**
 * The banner that held the slot stopped wanting it (× / sent feedback / downloaded): that counts as closing it for
 * this session. The connection banner going away does not (it was never dismissed).
 */
export function closedThisSession(input: { shown: TopBanner; wants: TopBannerWants }): boolean {
  return (input.shown === "beta" || input.shown === "getApp") && !input.wants[input.shown];
}

/** Reads the stored state (JSON); anything unreadable is a first visit. */
export function parseState(raw: string | null | undefined): BetaState {
  if (raw === null || raw === undefined || raw === "") return {};
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof json !== "object" || json === null || Array.isArray(json)) return {};
  const o = json as Record<string, unknown>;
  const out: BetaState = {};
  if (typeof o.dismissedAt === "number" && Number.isFinite(o.dismissedAt)) out.dismissedAt = o.dismissedAt;
  if (typeof o.dismissedVersion === "string") out.dismissedVersion = o.dismissedVersion;
  if (typeof o.feedbackSentAt === "number" && Number.isFinite(o.feedbackSentAt)) out.feedbackSentAt = o.feedbackSentAt;
  return out;
}

/** The stored form `parseState` reads (fixed key order, absent fields left out). */
export function serializeState(state: BetaState): string {
  const out: BetaState = {};
  if (state.dismissedAt !== undefined) out.dismissedAt = state.dismissedAt;
  if (state.dismissedVersion !== undefined) out.dismissedVersion = state.dismissedVersion;
  if (state.feedbackSentAt !== undefined) out.feedbackSentAt = state.feedbackSentAt;
  return JSON.stringify(out);
}
